import { useState, useEffect } from 'react';
import {
  Card, Tabs, Avatar, Tag, Button, Modal, Form, Input, Select, message,
  Popconfirm, Badge, Row, Col, Space, Empty, Table,
} from 'antd';
import {
  TeamOutlined, UserAddOutlined, SafetyOutlined,
  EditOutlined, CheckCircleFilled,
  CloseCircleFilled, ClockCircleFilled, ExclamationCircleFilled,
  DeleteOutlined, PlusOutlined, MailOutlined,
} from '@ant-design/icons';
import {
  getMembers, getRoles, getTasks, assignTask, updateTaskStatus,
  updateRole, inviteMember, removeMember, ROLES,
} from '../../api/team';

// —— 状态配置 ——
const STATUS_MAP = {
  online: { color: '#52c41a', label: '在线' },
  away: { color: '#faad14', label: '离开' },
  offline: { color: '#bfbfbf', label: '离线' },
};

const PRIORITY_MAP = {
  high: { color: '#f5222d', label: '高' },
  medium: { color: '#fa8c16', label: '中' },
  low: { color: '#52c41a', label: '低' },
};

const TASK_STATUS_MAP = {
  todo: { label: '待处理', color: '#d9d9d9', icon: <ClockCircleFilled /> },
  'in-progress': { label: '进行中', color: '#1890ff', icon: <ExclamationCircleFilled /> },
  done: { label: '已完成', color: '#52c41a', icon: <CheckCircleFilled /> },
};

// —— 子组件：成员列表 ——
const MemberList = ({ members, roles, onInvite, onRoleChange, onRemove, loading }) => {
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleInvite = async () => {
    const values = await form.validateFields();
    await onInvite(values.email, values.role);
    form.resetFields();
    setInviteModalOpen(false);
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }} />;

  return (
    <div>
      {/* 操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>
            团队成员
          </span>
          <Tag style={{ marginLeft: 8 }}>{members.length} 人</Tag>
        </div>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => setInviteModalOpen(true)}
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none', borderRadius: 8,
          }}
        >
          邀请成员
        </Button>
      </div>

      {/* 成员卡片网格 */}
      <Row gutter={[16, 16]}>
        {members.map((m) => {
          const roleInfo = roles[m.role] || {};
          const statusInfo = STATUS_MAP[m.status] || {};
          return (
            <Col key={m.id} xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                style={{
                  borderRadius: 12,
                  background: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  {/* 头像 + 状态指示灯 */}
                  <Badge
                    dot
                    color={statusInfo.color}
                    offset={[-6, 6]}
                    style={{ marginBottom: 12 }}
                  >
                    <Avatar
                      size={56}
                      src={m.avatar}
                      style={{
                        background: `linear-gradient(135deg, ${roleInfo.color || '#6366f1'}33, ${roleInfo.color || '#8b5cf6'}22)`,
                        border: `2px solid ${roleInfo.color || '#6366f1'}40`,
                      }}
                    >
                      {m.name[0]}
                    </Avatar>
                  </Badge>

                  {/* 姓名 */}
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                    {m.name}
                  </div>

                  {/* 角色彩色Tag */}
                  <Tag
                    color={roleInfo.color}
                    style={{ marginBottom: 8, borderRadius: 4, fontSize: 11 }}
                  >
                    {roleInfo.label || m.role}
                  </Tag>

                  {/* 邮箱 */}
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, wordBreak: 'break-all' }}>
                    <MailOutlined style={{ marginRight: 4 }} />
                    {m.email}
                  </div>

                  {/* 状态文字 */}
                  <div style={{ fontSize: 11, color: statusInfo.color, marginBottom: 12 }}>
                    <span style={{
                      display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                      background: statusInfo.color, marginRight: 4,
                    }} />
                    {statusInfo.label} · {new Date(m.lastActive).toLocaleDateString('zh-CN')}
                  </div>

                  {/* 操作 */}
                  <Space size={4}>
                    <Select
                      size="small"
                      value={m.role}
                      style={{ width: 100 }}
                      onChange={(val) => onRoleChange(m.id, val)}
                      disabled={m.role === 'admin'}
                      options={Object.entries(roles).map(([k, v]) => ({ value: k, label: v.label }))}
                    />
                    <Popconfirm
                      title="确认移除成员？"
                      description={`${m.name} 将被移出团队`}
                      onConfirm={() => onRemove(m.id)}
                      okText="确认移除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      disabled={m.role === 'admin'}
                    >
                      <Button
                        size="small"
                        danger
                        disabled={m.role === 'admin'}
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </Space>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* 邀请成员 Modal */}
      <Modal
        title={<span><UserAddOutlined style={{ marginRight: 8 }} />邀请新成员</span>}
        open={inviteModalOpen}
        onOk={handleInvite}
        onCancel={() => setInviteModalOpen(false)}
        okText="发送邀请"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="email"
            label="邮箱地址"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="colleague@simoncat.com" />
          </Form.Item>
          <Form.Item
            name="role"
            label="分配角色"
            rules={[{ required: true, message: '请选择角色' }]}
            initialValue="editor"
          >
            <Select
              options={Object.entries(roles).map(([k, v]) => ({ value: k, label: v.label }))}
              placeholder="选择角色"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// —— 子组件：角色权限矩阵 ——
const RolePermission = ({ roles, permissions, modules, loading }) => {
  const roleKeys = Object.keys(roles);

  const columns = [
    {
      title: '功能模块',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left',
      width: 130,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    ...roleKeys.map((roleKey) => ({
      title: () => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={roles[roleKey].color} style={{ borderRadius: 4 }}>
            {roles[roleKey].label}
          </Tag>
        </div>
      ),
      key: roleKey,
      width: 110,
      align: 'center',
      render: (_, record) => {
        const has = permissions[roleKey]?.[record.key];
        return has ? (
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: '#d9d9d9', fontSize: 18 }} />
        );
      },
    })),
  ];

  const dataSource = modules.map((m) => ({ ...m, key: m.key }));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>
          角色权限矩阵
        </span>
        <Tag style={{ marginLeft: 8 }} color="blue">4 角色 x 8 权限</Tag>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="key"
        pagination={false}
        loading={loading}
        bordered
        size="middle"
        scroll={{ x: 800 }}
      />
      <div style={{ marginTop: 16, display: 'flex', gap: 20, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span><CheckCircleFilled style={{ color: '#52c41a' }} /> 拥有权限</span>
        <span><CloseCircleFilled style={{ color: '#d9d9d9' }} /> 无权限</span>
      </div>
    </div>
  );
};

// —— 子组件：任务看板 ——
const TaskBoard = ({ tasks, members, onAssign, onStatusChange, loading }) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    const values = await form.validateFields();
    await onAssign({ ...values, assigneeId: Number(values.assigneeId) });
    form.resetFields();
    setCreateModalOpen(false);
  };

  // 按状态分组
  const grouped = {
    todo: tasks.filter((t) => t.status === 'todo'),
    'in-progress': tasks.filter((t) => t.status === 'in-progress'),
    done: tasks.filter((t) => t.status === 'done'),
  };

  const getMember = (id) => members.find((m) => m.id === id);

  const renderTaskCard = (task) => {
    const member = getMember(task.assigneeId);
    const priority = PRIORITY_MAP[task.priority] || {};
    return (
      <Card
        key={task.id}
        size="small"
        style={{
          marginBottom: 10,
          borderRadius: 10,
          background: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          cursor: 'default',
        }}
        styles={{ body: { padding: 14 } }}
      >
        {/* 优先级Tag */}
        <Tag
          color={priority.color}
          style={{ marginBottom: 8, borderRadius: 4, fontSize: 11 }}
        >
          {priority.label}优先级
        </Tag>

        {/* 任务标题 */}
        <div style={{
          fontSize: 14, fontWeight: 500, color: 'var(--text-primary)',
          marginBottom: 10, lineHeight: 1.5,
        }}>
          {task.title}
        </div>

        {/* 分配给 + 截止时间 */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 12, color: 'var(--text-secondary)',
        }}>
          {member && (
            <Space size={4}>
              <Avatar size={20} src={member.avatar} style={{ fontSize: 10 }}>
                {member.name[0]}
              </Avatar>
              <span>{member.name}</span>
            </Space>
          )}
          <span>
            <ClockCircleFilled style={{ marginRight: 4 }} />
            {task.deadline}
          </span>
        </div>

        {/* 状态变更 */}
        <div style={{ marginTop: 10 }}>
          <Select
            size="small"
            value={task.status}
            style={{ width: '100%' }}
            onChange={(val) => onStatusChange(task.id, val)}
            options={Object.entries(TASK_STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))}
          />
        </div>
      </Card>
    );
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }} />;

  return (
    <div>
      {/* 操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>
            任务看板
          </span>
          <Tag style={{ marginLeft: 8 }}>{tasks.length} 个任务</Tag>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalOpen(true)}
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none', borderRadius: 8,
          }}
        >
          新建任务
        </Button>
      </div>

      {/* 三列看板 */}
      <Row gutter={[16, 16]}>
        {Object.entries(TASK_STATUS_MAP).map(([status, cfg]) => (
          <Col key={status} xs={24} md={8}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.02)',
              borderRadius: 12,
              padding: 16,
              minHeight: 400,
              border: `1px solid ${cfg.color}20`,
            }}>
              {/* 列头 */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: 16, paddingBottom: 12,
                borderBottom: `2px solid ${cfg.color}30`,
              }}>
                <span style={{ fontSize: 18, color: cfg.color }}>{cfg.icon}</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{cfg.label}</span>
                <Tag
                  style={{ marginLeft: 'auto', borderRadius: 10 }}
                  color={cfg.color}
                >
                  {grouped[status]?.length || 0}
                </Tag>
              </div>

              {/* 任务卡片 */}
              {grouped[status]?.length > 0 ? (
                grouped[status].map(renderTaskCard)
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无任务" />
              )}
            </div>
          </Col>
        ))}
      </Row>

      {/* 新建任务 Modal */}
      <Modal
        title={<span><PlusOutlined style={{ marginRight: 8 }} />新建任务</span>}
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => setCreateModalOpen(false)}
        okText="创建任务"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="任务标题"
            rules={[{ required: true, message: '请输入任务标题' }]}
          >
            <Input placeholder="例如：撰写小红书推广文案" />
          </Form.Item>
          <Form.Item
            name="assigneeId"
            label="分配给"
            rules={[{ required: true, message: '请选择负责人' }]}
          >
            <Select
              placeholder="选择团队成员"
              options={members.map((m) => ({
                value: m.id,
                label: (
                  <Space>
                    <Avatar size={20} src={m.avatar}>{m.name[0]}</Avatar>
                    {m.name} ({ROLES[m.role]?.label})
                  </Space>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label="优先级"
            rules={[{ required: true, message: '请选择优先级' }]}
            initialValue="medium"
          >
            <Select
              options={[
                { value: 'high', label: '高优先级' },
                { value: 'medium', label: '中优先级' },
                { value: 'low', label: '低优先级' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="deadline"
            label="截止时间"
            rules={[{ required: true, message: '请选择截止时间' }]}
          >
            <Input type="date" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// —— 主组件 ——
const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState({});
  const [modules, setModules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');

  // 加载数据
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [m, r, t] = await Promise.all([
          getMembers(),
          getRoles(),
          getTasks(),
        ]);
        setMembers(m);
        setRoles(r.roles);
        setPermissions(r.permissions);
        setModules(r.modules);
        setTasks(t);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // 操作处理
  const handleInvite = async (email, role) => {
    await inviteMember(email, role);
    setMembers(await getMembers());
    message.success(`已邀请 ${email}`);
  };

  const handleRoleChange = async (memberId, newRole) => {
    await updateRole(memberId, newRole);
    setMembers(await getMembers());
    message.success('角色已更新');
  };

  const handleRemove = async (memberId) => {
    await removeMember(memberId);
    setMembers(await getMembers());
    message.success('成员已移除');
  };

  const handleAssign = async (taskData) => {
    await assignTask(taskData);
    setTasks(await getTasks());
    message.success('任务已创建');
  };

  const handleTaskStatusChange = async (taskId, status) => {
    await updateTaskStatus(taskId, status);
    setTasks(await getTasks());
  };

  const tabItems = [
    {
      key: 'members',
      label: (
        <span>
          <TeamOutlined /> 成员管理
        </span>
      ),
      children: (
        <MemberList
          members={members}
          roles={roles}
          onInvite={handleInvite}
          onRoleChange={handleRoleChange}
          onRemove={handleRemove}
          loading={loading}
        />
      ),
    },
    {
      key: 'permissions',
      label: (
        <span>
          <SafetyOutlined /> 角色权限
        </span>
      ),
      children: (
        <RolePermission
          roles={roles}
          permissions={permissions}
          modules={modules}
          loading={loading}
        />
      ),
    },
    {
      key: 'tasks',
      label: (
        <span>
          <EditOutlined /> 任务分配
        </span>
      ),
      children: (
        <TaskBoard
          tasks={tasks}
          members={members}
          onAssign={handleAssign}
          onStatusChange={handleTaskStatusChange}
          loading={loading}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          <TeamOutlined style={{ marginRight: 10, color: 'var(--accent)' }} />
          团队协同
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: 6, fontSize: 13 }}>
          管理团队成员、分配角色权限、跟踪任务进度 — 对标 Salesforce 团队模式
        </p>
      </div>

      {/* Tab 内容 */}
      <Card
        style={{
          borderRadius: 14,
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
        styles={{ body: { padding: '20px 24px' } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
          tabBarStyle={{
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            marginBottom: 0,
          }}
        />
      </Card>
    </div>
  );
};

export default TeamPage;
